import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TONE, type Tone } from "@recursive-staff/ui";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

export function Projects() {
  const { data, isLoading, error } = useQuery({ queryKey: ["projects"], queryFn: api.projects });
  const [showNew, setShowNew] = useState(false);
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const behind = data.filter((p) => p.warnings.some((w) => w.level === "behind"));

  return (
    <Page>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Projects</h1>
          <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>Implementation & delivery work with schedule-health warnings</div>
        </div>
        <button onClick={() => setShowNew((v) => !v)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#fff", background: showNew ? "#6b706a" : "#006747", border: 0, borderRadius: 8, padding: "8px 13px", cursor: "pointer", flex: "none" }}>
          <Icon name={showNew ? "chevron-down" : "plus"} size={14} color="#fff" /> New project
        </button>
      </div>

      {showNew && <NewProject onDone={() => setShowNew(false)} />}

      {behind.length > 0 && (
        <div style={{ background: "#fbe9e7", border: "1px solid #f3d3ce", borderRadius: 6, padding: "12px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <Icon name="alert-triangle" size={15} color="#b3261e" />
            <span style={{ fontSize: 13, fontWeight: 600, color: "#b3261e" }}>Falling behind — {behind.length} {behind.length === 1 ? "project" : "projects"}</span>
          </div>
          {behind.map((p) => (
            <div key={p.id} style={{ fontSize: 12.5, color: "#7a2a24", marginTop: 2 }}>
              <strong>{p.name}:</strong> {p.warnings.find((w) => w.level === "behind")?.message}
            </div>
          ))}
        </div>
      )}

      {data.length === 0 && !showNew && (
        <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "28px 18px", textAlign: "center", color: "#8a8e88" }}>
          <div style={{ fontSize: 13.5, marginBottom: 10 }}>No projects yet.</div>
          <button onClick={() => setShowNew(true)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12.5, fontWeight: 600, color: "#fff", background: "#006747", border: 0, borderRadius: 8, padding: "8px 14px", cursor: "pointer" }}>
            <Icon name="plus" size={14} color="#fff" /> Create your first project
          </button>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(420px,1fr))", gap: 14 }}>
        {data.map((p) => (
          <Link key={p.id} to="/projects/$projectId" params={{ projectId: p.id }} style={{ display: "block", textDecoration: "none", color: "inherit", background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "16px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 15, fontWeight: 600, flex: 1 }}>{p.name}</span>
              <span style={{ fontSize: 10.5, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: TONE[(p.scheduleHealth as Tone) ?? "neutral"].bg, color: TONE[(p.scheduleHealth as Tone) ?? "neutral"].fg }}>{p.status}</span>
            </div>
            <div style={{ fontSize: 12, color: "#8a8e88", marginTop: 3 }}>{p.clientName} · PM {p.pm} · go-live {p.goLiveDate}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
              <div style={{ flex: 1, height: 7, background: "#f0efe9", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ width: `${p.progressPct}%`, height: "100%", background: "#006747" }} />
              </div>
              <span style={{ fontSize: 11.5, fontFamily: "'Space Mono',monospace" }}>{p.progressPct}%</span>
            </div>
            {p.warnings.length > 0 && (
              <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                {p.warnings.map((w, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 12, color: w.level === "behind" ? "#b3261e" : "#9a6a12" }}>
                    <Icon name={w.level === "behind" ? "alert-triangle" : "circle"} size={12} color={w.level === "behind" ? "#b3261e" : "#9a6a12"} style={{ marginTop: 2 }} />
                    <span>{w.message}</span>
                  </div>
                ))}
              </div>
            )}
          </Link>
        ))}
      </div>
    </Page>
  );
}

const NP_FIELD: React.CSSProperties = { fontSize: 12.5, padding: "8px 10px", border: "1px solid #e2e0d8", borderRadius: 7, background: "#fff", color: "#1d241f" };

// New-project form: a blank project (one "Tasks" list) or start from a template.
function NewProject({ onDone }: { onDone: () => void }) {
  const qc = useQueryClient();
  const clients = useQuery({ queryKey: ["tenant-clients"], queryFn: api.tenantClients });
  const templates = useQuery({ queryKey: ["templates"], queryFn: api.templates });
  const [name, setName] = useState("");
  const [clientId, setClientId] = useState("");
  const [mode, setMode] = useState<"blank" | "template">("blank");
  const [templateId, setTemplateId] = useState("");

  const done = () => { qc.invalidateQueries({ queryKey: ["projects"] }); onDone(); };
  const createBlank = useMutation({ mutationFn: () => api.createProject(name.trim(), clientId), onSuccess: done });
  const fromTemplate = useMutation({ mutationFn: () => api.applyTemplate(templateId, clientId, name.trim()), onSuccess: done });
  const busy = createBlank.isPending || fromTemplate.isPending;
  const canCreate = !!name.trim() && !!clientId && (mode === "blank" || !!templateId) && !busy;
  const err = createBlank.error || fromTemplate.error;
  const submit = () => { if (!canCreate) return; if (mode === "blank") createBlank.mutate(); else fromTemplate.mutate(); };

  const tabStyle = (active: boolean): React.CSSProperties => ({ fontSize: 12.5, fontWeight: 600, padding: "6px 12px", borderRadius: 7, border: `1px solid ${active ? "#006747" : "#e2e0d8"}`, background: active ? "#eef4f0" : "#fff", color: active ? "#006747" : "#6b706a", cursor: "pointer" });

  return (
    <div style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: 16, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 600 }}>New project</div>
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={() => setMode("blank")} style={tabStyle(mode === "blank")}>Blank</button>
        <button onClick={() => setMode("template")} style={tabStyle(mode === "template")}>From template</button>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Project name" style={{ ...NP_FIELD, flex: "1 1 220px" }} />
        <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={NP_FIELD}>
          <option value="">— choose client —</option>
          {(clients.data ?? []).map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
        </select>
        {mode === "template" && (
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} style={NP_FIELD}>
            <option value="">— choose template —</option>
            {(templates.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name} ({t.taskCount})</option>)}
          </select>
        )}
        <button onClick={submit} disabled={!canCreate} style={{ fontSize: 12.5, fontWeight: 600, color: "#fff", background: canCreate ? "#006747" : "#b7c3bd", border: 0, borderRadius: 8, padding: "8px 16px", cursor: canCreate ? "pointer" : "default" }}>
          {busy ? "Creating…" : "Create"}
        </button>
      </div>
      {mode === "template" && (templates.data ?? []).length === 0 && (
        <div style={{ fontSize: 11.5, color: "#8a8e88" }}>No templates yet — create one on the Task templates screen, or use a blank project.</div>
      )}
      {err && <div style={{ fontSize: 11.5, color: "#b3261e" }}>{err instanceof Error ? err.message : "Couldn't create the project"}</div>}
    </div>
  );
}
